import { AlertCircle, Check, Clock, Search, TrendingDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { bucketSubscriptionStatus } from '../lib/utils';
import { useAuthStore } from '../store/auth';

interface Agency {
  id: string;
  name: string;
  email: string;
  owner_name: string;
  subscription_status: string;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  customer_count: number;
  vehicle_count: number;
  agent_count: number;
  total_events: number;
  created_at: string;
}

interface SubscriptionStats {
  active_count: number;
  trial_count: number;
  expired_count: number;
  expiring_soon_count: number;
  avg_subscription_length: number;
}

export default function SuperAdminSubscriptions() {
  const token = useAuthStore((s) => s.accessToken);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'trial' | 'expired'>('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const limit = 15;

  useEffect(() => {
    fetchSubscriptionData();
  }, [page, search, filterStatus]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Fetch all agencies data for stats (no pagination, large limit)
      const statsParams = new URLSearchParams({
        page: '1',
        limit: '1000', // Large limit to get all agencies for stats
      });
      if (search) {
        statsParams.append('search', search);
      }

      const statsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/agencies?${statsParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!statsResponse.ok) throw new Error('Failed to fetch agencies');
      const statsData = await statsResponse.json();
      const allAgencies = statsData.data || [];

      // Calculate stats from all agencies
      const stats: SubscriptionStats = {
        active_count: allAgencies.filter((a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'active').length,
        trial_count: allAgencies.filter((a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'trial').length,
        expired_count: allAgencies.filter((a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'expired').length,
        expiring_soon_count: allAgencies.filter((a: Agency) => {
          if (!a.subscription_ends_at && !a.trial_ends_at) return false;
          const expiryDate = new Date(a.subscription_ends_at || a.trial_ends_at || '');
          const daysUntilExpiry =
            (expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
          return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
        }).length,
        avg_subscription_length: 12, // Placeholder
      };
      setStats(stats);

      // Now fetch paginated data for table display
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) {
        params.append('search', search);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/agencies?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch agencies');
      const data = await response.json();

      let filteredAgencies = data.data || [];
      if (filterStatus !== 'all') {
        filteredAgencies = filteredAgencies.filter(
          (a: Agency) => bucketSubscriptionStatus(a.subscription_status) === filterStatus
        );
      }

      setAgencies(filteredAgencies);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilExpiry = (expiryDate: string | null) => {
    if (!expiryDate) return null;
    const daysRemaining = Math.ceil(
      (new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysRemaining;
  };

  const getExpiryStatus = (agency: Agency) => {
    const expiryDate = agency.subscription_ends_at || agency.trial_ends_at;
    const daysLeft = getDaysUntilExpiry(expiryDate);

    if (!daysLeft) return { label: 'No expiry', color: 'gray' };
    if (daysLeft < 0) return { label: 'Expired', color: 'red' };
    if (daysLeft <= 7) return { label: 'Critical', color: 'red' };
    if (daysLeft <= 30) return { label: 'Warning', color: 'orange' };
    return { label: 'Healthy', color: 'green' };
  };

  const getStatusBadge = (status: string) => {
    const baseClass = 'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold';
    switch (bucketSubscriptionStatus(status)) {
      case 'active':
        return `${baseClass} bg-green-100 text-green-800`;
      case 'trial':
        return `${baseClass} bg-blue-100 text-blue-800`;
      case 'expired':
        return `${baseClass} bg-red-100 text-red-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const getSortedAgencies = () => {
    let sorted = [...agencies];
    sorted.sort((a, b) => {
      const dateA = getDaysUntilExpiry(a.subscription_ends_at || a.trial_ends_at) ?? 999999;
      const dateB = getDaysUntilExpiry(b.subscription_ends_at || b.trial_ends_at) ?? 999999;
      return dateA - dateB;
    });
    return sorted;
  };

  if (loading && !stats) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-gray-600">Loading subscriptions...</div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
          <p className="text-gray-600 mt-2">
            Track subscription periods, manage renewals, and monitor trial agencies
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Active Subscriptions */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Subscriptions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.active_count || 0}</p>
              </div>
              <Check className="w-12 h-12 text-green-500 opacity-20" />
            </div>
          </div>

          {/* Trial Agencies */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Trial Agencies</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.trial_count || 0}</p>
              </div>
              <Clock className="w-12 h-12 text-blue-500 opacity-20" />
            </div>
          </div>

          {/* Expiring Soon */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Expiring Soon</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.expiring_soon_count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Within 7 days</p>
              </div>
              <AlertCircle className="w-12 h-12 text-orange-500 opacity-20" />
            </div>
          </div>

          {/* Expired Subscriptions */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Expired</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.expired_count || 0}</p>
              </div>
              <TrendingDown className="w-12 h-12 text-red-500 opacity-20" />
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by agency name or email..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <div className="flex gap-2">
                {(['all', 'active', 'trial', 'expired'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setFilterStatus(status);
                      setPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === status
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Subscription Details</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Agency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Ends at
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Days Left
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Customers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Health
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </td>
                  </tr>
                ) : getSortedAgencies().length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No agencies found
                    </td>
                  </tr>
                ) : (
                  getSortedAgencies().map((agency) => {
                    const expiryStatus = getExpiryStatus(agency);
                    const daysLeft = getDaysUntilExpiry(
                      agency.subscription_ends_at || agency.trial_ends_at
                    );
                    return (
                      <tr key={agency.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{agency.name}</p>
                            <p className="text-xs text-gray-600">{agency.owner_name}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={getStatusBadge(agency.subscription_status)}>
                            {agency.subscription_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {bucketSubscriptionStatus(agency.subscription_status) === 'trial' ? 'Trial' : 'Paid'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {agency.subscription_ends_at
                            ? formatDate(agency.subscription_ends_at)
                            : agency.trial_ends_at
                              ? formatDate(agency.trial_ends_at)
                              : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {daysLeft !== null ? (
                            <span
                              className={`text-sm font-semibold ${
                                daysLeft < 0
                                  ? 'text-red-600'
                                  : daysLeft <= 7
                                    ? 'text-red-600'
                                    : daysLeft <= 30
                                      ? 'text-orange-600'
                                      : 'text-green-600'
                              }`}
                            >
                              {daysLeft < 0 ? 'Expired' : `${daysLeft} days`}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-600">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {agency.customer_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                expiryStatus.color === 'green'
                                  ? 'bg-green-500'
                                  : expiryStatus.color === 'orange'
                                    ? 'bg-orange-500'
                                    : 'bg-red-500'
                              }`}
                            ></div>
                            <span
                              className={`text-xs font-medium ${
                                expiryStatus.color === 'green'
                                  ? 'text-green-600'
                                  : expiryStatus.color === 'orange'
                                    ? 'text-orange-600'
                                    : 'text-red-600'
                              }`}
                            >
                              {expiryStatus.label}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {agencies.length} agencies
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(Math.max(1, page - 1))}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-gray-600 text-sm font-medium">Healthy Subscriptions</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {(stats?.active_count || 0) + (stats?.trial_count || 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Active and trial agencies</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm font-medium">At Risk</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">
                {stats?.expiring_soon_count || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Expiring within 7 days</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm font-medium">Action Required</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {stats?.expired_count || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Expired subscriptions</p>
            </div>
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
